import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Layout, Avatar, Badge, Dropdown, Tooltip } from 'antd';
import {
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  BellOutlined,
  HomeOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { logout } from '../features/auth/authSlice';
import { setMenuData } from '../features/menu/menuSlice';
import GlobalNotification from './GlobalNotification';
import DynamicMenu from './DynamicMenu';
import { useWebSocket } from '../contexts/WebSocketContext';
import '../styles/layout.css';

const { Header, Sider, Content } = Layout;

const ROLE_LABELS = {
  super_admin: '超级管理员',
  admin: '管理员',
  operator: '操作员',
};

// 菜单之外的路由标题，用于顶栏面包屑兜底
const EXTRA_PAGE_TITLES = {
  '/dashboard': '综合看板',
  '/profile': '个人资料',
  '/settings': '系统设置',
};

const findMenuName = (nodes, path) => {
  for (const node of nodes || []) {
    if (node.route_path === path) return node.menu_name;
    const inChildren = findMenuName(node.children, path);
    if (inChildren) return inChildren;
  }
  return null;
};

const BrandMark = () => (
  <svg className="app-logo__mark" viewBox="0 0 32 32" aria-hidden="true">
    <defs>
      <linearGradient id="appBrandMark" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#5aacff" />
        <stop offset="1" stopColor="#1657cf" />
      </linearGradient>
    </defs>
    <circle cx="16" cy="16" r="15" fill="url(#appBrandMark)" />
    <path
      d="M21.6 11.3a7.1 7.1 0 1 0 0 9.4"
      fill="none"
      stroke="#fff"
      strokeWidth="3"
      strokeLinecap="round"
    />
    <circle cx="16" cy="16" r="2.3" fill="#fff" />
  </svg>
);

const AppLayout = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const menuData = useSelector((state) => state.menu);
  const { isConnected } = useWebSocket();

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const handleMenuLoad = (data) => {
    // 将菜单和权限信息存储到 Redux
    dispatch(setMenuData(data));
  };

  const roleLabel = ROLE_LABELS[menuData?.role] || '';
  const pageTitle = findMenuName(menuData?.menuTree, location.pathname)
    || EXTRA_PAGE_TITLES[location.pathname]
    || '';

  const userMenuItems = [
    ...(roleLabel ? [{ key: 'role', type: 'group', label: `${roleLabel}控制台` }] : []),
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人资料',
      onClick: () => navigate('/profile'),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: '系统设置',
      onClick: () => navigate('/settings'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: handleLogout,
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* 全局 WebSocket 通知 */}
      <GlobalNotification />

      <Sider className="app-sider" width={240}>
        <div className="app-logo">
          <BrandMark />
          <span className="app-logo__text">金融后台管理系统</span>
        </div>

        <DynamicMenu onMenuLoad={handleMenuLoad} />
      </Sider>

      <Layout className="app-body" style={{ marginLeft: 240 }}>
        <Header className="app-header">
          <div className="app-crumb">
            <button
              type="button"
              className="app-crumb__home"
              onClick={() => navigate('/dashboard')}
              aria-label="返回综合看板"
            >
              <HomeOutlined />
            </button>
            {pageTitle && (
              <>
                <span className="app-crumb__sep" />
                <span className="app-crumb__title">{pageTitle}</span>
              </>
            )}
          </div>

          <div className="app-header__right">
            {/* 红点同时表达 WebSocket 连接状态，断线时提示用户 */}
            <Tooltip title={isConnected ? '实时通知已连接' : '实时通知未连接'}>
              <button type="button" className="app-icon-btn">
                <Badge dot color={isConnected ? '#52c41a' : '#ff4d4f'} offset={[1, 1]}>
                  <BellOutlined />
                </Badge>
              </button>
            </Tooltip>

            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" arrow>
              <div className="app-user">
                <Avatar
                  size={30}
                  icon={<UserOutlined />}
                  src={user?.avatar}
                  style={{ background: '#cddaea', color: '#fff' }}
                />
                <span className="app-user__name">{user?.name || user?.phone}</span>
                <DownOutlined className="app-user__caret" />
              </div>
            </Dropdown>
          </div>
        </Header>

        <Content className="app-content">
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

export default AppLayout;
